import { MenuItem, type MenuItemSpec } from "prosemirror-menu";
import type { MarkType, Node, NodeType } from "prosemirror-model";
import { NodeSelection } from "prosemirror-state";
import type { EditorState, Selection, Transaction } from "prosemirror-state";

import { schema } from "../adapter/schema";
import { getSharedAttrs } from "../utils";

/**
 * Returns true if all top-level nodes in the selection are of the given type.
 *
 * If the selection is a NodeSelection, only the selected node is checked.
 * Otherwise, top-level nodes are checked without recursing into children.
 */
function allSelectedNodesAreType(selection: Selection, doc: Node, nodeType: NodeType): boolean {
  if (selection instanceof NodeSelection) {
    return selection.node.type === nodeType;
  }

  let result = true;
  doc.nodesBetween(selection.from, selection.to, (node) => {
    if (node.type !== nodeType) result = false;
    return false; // Never recurse into child nodes
  });
  return result;
}

/**
 * Change the type of all top-level nodes in the selection to `targetType`,
 * preserving shared attrs (align, indent) from each existing node.
 * Optionally strips inline marks (e.g. when converting to code_block).
 */
function setBlockTypeKeepSharedAttrs(
  state: EditorState,
  dispatch: ((tr: Transaction) => void) | undefined,
  targetType: NodeType,
  marksToStrip: MarkType[] = [],
): boolean {
  const { selection, doc, tr } = state;

  doc.nodesBetween(selection.from, selection.to, (node, pos) => {
    if (node.type !== targetType) {
      // removeMark only adds steps for spans where the mark actually exists, so this is a no-op for unmarked text
      for (const markType of marksToStrip) {
        tr.removeMark(pos + 1, pos + node.nodeSize - 1, markType);
      }
      tr.setBlockType(pos, pos + node.nodeSize, targetType, getSharedAttrs(node));
    }
    return false; // Never recurse into child nodes
  });

  dispatch?.(tr.scrollIntoView());
  return true;
}

/**
 * This is based on `blockTypeItem` from `prosemirror-menu`,
 * but it toggles between the primary node type and the secondary (e.g., paragraph).
 */
export function toggleBlockTypeItem(
  primaryNodeType: NodeType,
  secondaryNodeType: NodeType,
  options: Partial<MenuItemSpec>,
) {
  const marksDisallowedInCodeBlock: MarkType[] = [
    schema.marks.color,
    schema.marks.bold,
    schema.marks.italic,
    schema.marks.underline,
    schema.marks.link,
  ];

  const passedOptions: MenuItemSpec = {
    run: (state, dispatch) => {
      const isPrimaryType = allSelectedNodesAreType(state.selection, state.doc, primaryNodeType);
      const targetType = isPrimaryType ? secondaryNodeType : primaryNodeType;
      const marksToStrip = targetType === schema.nodes.code_block ? marksDisallowedInCodeBlock : [];
      return setBlockTypeKeepSharedAttrs(state, dispatch, targetType, marksToStrip);
    },
    active(state) {
      return allSelectedNodesAreType(state.selection, state.doc, primaryNodeType);
    },
  };
  Object.assign(passedOptions, options);
  return new MenuItem(passedOptions);
}
